import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  IconButton,
  Modal,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import React from "react";

import { PerformanceTab } from "../../viewer/tabs/PerformanceTab";
import { ADJACENT_OFFSET } from "../constants/numeric";
import { LensHistoryItem } from "../utils/lensHistoryStorage";
import { LENS_NAVIGATION_DIRECTION, LensNavigationDirection } from "../utils/lensNavigation";

const MODAL_WIDTH_PERCENTAGE = "80%";
const MODAL_MAX_WIDTH = "900px";
const MODAL_HEIGHT_PERCENTAGE = "80%";
const INITIAL_MODAL_INDEX = 0;

interface HistoryModalProps {
  open: boolean;
  onClose: () => void;
  currentItem: LensHistoryItem | null;
  modalIndex: number;
  totalCount: number;
  onNavigate: (direction: LensNavigationDirection) => void;
}

interface ModalNavigationProps {
  interactionId?: string;
  modalIndex: number;
  totalCount: number;
  onNavigate: (direction: LensNavigationDirection) => void;
  onClose: () => void;
}

const ModalNavigation = ({ interactionId, modalIndex, totalCount, onNavigate, onClose }: ModalNavigationProps): React.ReactElement => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
    <Typography variant="h6" id="all-history-modal-title">
      <strong>Lens Tester Results</strong>{interactionId ? `: ${interactionId}` : ""}
    </Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Tooltip title="Previous result" placement="bottom">
        <span>
          <IconButton onClick={() => onNavigate(LENS_NAVIGATION_DIRECTION.Previous)} disabled={modalIndex <= INITIAL_MODAL_INDEX}>
            <ArrowBackIosIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Typography variant="body2" sx={{ mx: 1, minWidth: "60px", textAlign: "center" }}>
        {`${modalIndex + ADJACENT_OFFSET}/${totalCount}`}
      </Typography>
      <Tooltip title="Next result" placement="bottom">
        <span>
          <IconButton onClick={() => onNavigate(LENS_NAVIGATION_DIRECTION.Next)} disabled={modalIndex >= totalCount - ADJACENT_OFFSET}>
            <ArrowForwardIosIcon />
          </IconButton>
        </span>
      </Tooltip>
      <IconButton onClick={onClose} aria-label="close modal">
        <CloseIcon />
      </IconButton>
    </Box>
  </Box>
);

const HistoryModalContent = ({ currentItem, modalIndex, totalCount, onNavigate, onClose }: Omit<HistoryModalProps, "open">): React.ReactElement => {
  if (!currentItem) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
        <Typography variant="h6" color="text.secondary">No test results available</Typography>
      </Box>
    );
  }

  return (
    <>
      <ModalNavigation
        interactionId={currentItem.interactionId}
        modalIndex={modalIndex}
        totalCount={totalCount}
        onNavigate={onNavigate}
        onClose={onClose}
      />
      <Stack spacing={3}>
        <TextField label="Tenant Name" value={currentItem.tenantName || "N/A"} InputProps={{ readOnly: true }} fullWidth />
        <TextField label="Test Name & version" value={currentItem.name || "Unnamed Test"} InputProps={{ readOnly: true }} fullWidth />
        <Box>
          <Typography variant="subtitle1" gutterBottom>Lens Used</Typography>
          <TextField multiline rows={8} value={currentItem.content} InputProps={{ readOnly: true }} fullWidth />
        </Box>
        <Box>
          <Typography variant="subtitle1" gutterBottom>Test Output</Typography>
          <Paper elevation={1} sx={{ p: 2, bgcolor: "grey.50" }}>
            {currentItem.lastResult ? (
              <PerformanceTab
                data={{
                  scorecard: {
                    content: { scorecard: currentItem.lastResult },
                    type: "scorecard",
                    modified: currentItem.createdAt
                  },
                  all: []
                }}
                isLoading={false}
                expandedAttribute="modal_result"
              />
            ) : (
              <Typography color="text.secondary">No test results available</Typography>
            )}
          </Paper>
        </Box>
      </Stack>
    </>
  );
};

const LensHistoryModal = ({
  open, onClose, currentItem, modalIndex, totalCount, onNavigate
}: HistoryModalProps): React.ReactElement => (
  <Modal
    open={open}
    onClose={onClose}
    aria-labelledby="all-history-modal-title"
    aria-describedby="all-history-modal-description"
  >
    <Box sx={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: MODAL_WIDTH_PERCENTAGE,
      maxWidth: MODAL_MAX_WIDTH,
      height: MODAL_HEIGHT_PERCENTAGE,
      bgcolor: "background.paper",
      border: "2px solid #000",
      boxShadow: 24,
      p: 4,
      overflow: "auto"
    }}>
      <HistoryModalContent
        onClose={onClose}
        currentItem={currentItem}
        modalIndex={modalIndex}
        totalCount={totalCount}
        onNavigate={onNavigate}
      />
    </Box>
  </Modal>
);

export default LensHistoryModal;
